
import _ from 'underscore';
import CryptoJS from 'crypto-js';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { usePostABTestingEventLog as useOverreactPostABTestingEventLog } from '@bingads-webui/overreact-odata-campaign';

// Please refer to wiki here: https://msasg.visualstudio.com/DefaultCollection/Bing_Ads/_wiki/wikis/Bing_Ads.wiki/80390/Flight-Event-APIs-for-AB-Testing-Logging

export function useOnceEffect(callFn) {
  const [called, setCalled] = useState(false);

  useEffect(() => {
    if (!called && callFn()) {
      setCalled(true);
    }
  }, [callFn, called]);
}

export function nullableValue(obj, key) {
  const value = (obj && obj[key]);
  if (_.isNull(value) || _.isUndefined(value)) {
    return null;
  }
  return value;
}

export function getSharedValues(serverContext) {
  const {
    CurrentAccount,
    CurrentCustomer,
    Omniture,
    PageTrackingId,
    Identity,
    UserRegion,
  } = serverContext || {};

  const { SessionId } = Omniture || {};
  const { UserName } = Identity || {};
  const HashedUserName = CryptoJS.MD5(UserName).toString();

  return {
    AID: nullableValue(CurrentAccount, 'Id'),
    CID: nullableValue(CurrentCustomer, 'Id'),
    Mode: nullableValue(CurrentAccount, 'AccountMode'),
    SessionId,
    PageTrackingId,
    UserName,
    HashedUserName,
    UserRegion,
  };
}

export function getDescriptorIds(serverContext) {
  const {
    CurrentAccount,
    CurrentCustomer,
    CurrentUser,
  } = serverContext || {};

  // Fallback to "0" if user wasn't created yet
  const aid = (CurrentAccount && CurrentAccount.Id) || 0;
  const cid = (CurrentCustomer && CurrentCustomer.Id) || 0;
  const uid = (CurrentUser && CurrentUser.Id) || 0;

  return {
    uid,
    cid,
    aid,
    customer_id: cid,
    account_id: aid,
  };
}

export const postponeRead = {
  fetch: {
    postponeRead: true,
  },
};

export function usePostABTestingEventLog(serverContext, logTrace) {
  const accountLevelVariables = useMemo(() => {
    const descriptor = getDescriptorIds(serverContext);

    return {
      locator: {
        descriptor,
      },
    };
  }, [serverContext]);

  const [, { callFn: postABTestingEventLog }] =
    useOverreactPostABTestingEventLog(accountLevelVariables, postponeRead);

  return useCallback((args) => {
    const {
      UniqueId,
      EventName,
    } = args;
    const EventValue = _.isObject(args.EventValue) ? JSON.stringify(args.EventValue) : `${args.EventValue}`;

    logTrace(`[PostABTestingEventLog]: UniqueId: ${UniqueId}, EventName: ${EventName}, EventValue: ${EventValue}`);

    const data = {
      UniqueId,
      EventName,
      EventValue,
    };
    postABTestingEventLog({
      payload: { data },
    });
  }, [logTrace, postABTestingEventLog]);
}
